import { Check } from "lucide-react-native";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Platform } from "react-native";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  ButtonText,
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
  Heading,
  HStack,
  Input,
  InputField,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import {
  DemographicOption,
  DemographicSetting,
  Panel,
} from "@/types/resources";

import DemographicsEditor from "./DemographicsEditor";

interface PanelEditorProps {
  onClose: () => void;
  onSave: (panel: Panel) => void;
  panel: Panel | null;
}

const PanelEditor: FC<PanelEditorProps> = ({ onClose, onSave, panel }) => {
  const { i18n } = useLocalization();
  const [name, setName] = useState(panel?.name ?? "");
  const nameRef = useRef(panel?.name ?? "");
  const [canSaveName, setCanSaveName] = useState(!!panel?.name.trim());
  const [isDefault, setIsDefault] = useState(panel?.is_default ?? false);
  const [demographics, setDemographics] = useState<
    DemographicSetting<Exclude<DemographicOption, "kids_age">>[]
  >(panel?.demographics ?? []);

  const { error, loading, updatePanel } = API.Panels.useUpdatePanel();
  const {
    createPanel,
    error: createError,
    loading: createLoading,
  } = API.Panels.useCreatePanel();

  const isValidDemographics = useCallback(() => {
    if (demographics.length === 0) return false;

    for (const demographic of demographics) {
      const invalidWeights = demographic.distribution.some(
        (dist) => dist.weight < 0
      );
      if (invalidWeights) return false;

      const totalWeight = demographic.distribution.reduce(
        (sum, d) => sum + d.weight,
        0
      );
      if (totalWeight < 0.0001) return false; // Must be > 0
      if (totalWeight > 1.0001) return false; // Must be <= 100%
    }

    return true;
  }, [demographics]);

  const isSaveDisabled = useCallback(() => {
    return !canSaveName || !isValidDemographics();
  }, [canSaveName, isValidDemographics]);

  const hasUnsavedChanges = useCallback(() => {
    if (!panel) return false;
    return (
      nameRef.current !== panel.name ||
      isDefault !== panel.is_default ||
      JSON.stringify(demographics) !== JSON.stringify(panel.demographics)
    );
  }, [demographics, isDefault, panel]);

  // Sync the form state from the panel prop during render (not in an effect)
  // whenever it changes. The initial values are seeded above via lazy state.
  const [prevPanel, setPrevPanel] = useState(panel);
  if (prevPanel !== panel) {
    setPrevPanel(panel);
    if (panel) {
      setName(panel.name);
      setCanSaveName(!!panel.name.trim());
      setIsDefault(panel.is_default);
      setDemographics(panel.demographics);
    } else {
      setName("");
      setCanSaveName(false);
      setIsDefault(false);
      setDemographics([]);
    }
  }

  // Keep the latest-name ref in sync with the default name. Typing updates the
  // ref directly via the input's onChangeText, so this only fires when the
  // panel-driven default changes.
  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  const handleSave = useCallback(async () => {
    if (!panel) return;
    if (panel.id) {
      const updatedPanel = await updatePanel(panel.id, {
        demographics,
        description: null,
        folder_id: panel.folder_id,
        is_default: isDefault,
        name: nameRef.current,
      });
      if (updatedPanel) {
        onSave(updatedPanel);
        onClose();
      }
    } else {
      const newPanel = await createPanel({
        demographics,
        description: null,
        folder_id: panel.folder_id,
        is_default: isDefault,
        name: nameRef.current,
      });

      if (newPanel) {
        onSave(newPanel);
        onClose();
      }
    }
  }, [
    createPanel,
    demographics,
    isDefault,
    onClose,
    onSave,
    panel,
    updatePanel,
  ]);

  // Submit on Enter key
  useEffect(() => {
    if (Platform.OS !== "web" || panel === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isSaveDisabled() &&
        !loading &&
        !createLoading
      ) {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  // Warn on page leave with unsaved changes
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const saving = loading || createLoading;
  const errorMessage = error || createError;

  return (
    <AlertDialog isOpen={panel !== null} onClose={onClose} size="lg">
      <AlertDialogBackdrop />
      <AlertDialogContent className="gap-6 max-w-3xl h-[95vh]">
        <AlertDialogHeader>
          <Heading size="lg">
            {panel?.id
              ? i18n.t("panels.editPanel")
              : i18n.t("panels.createPanel")}
          </Heading>
        </AlertDialogHeader>
        <AlertDialogBody className="flex-1">
          <VStack className="space-y-10">
            <VStack className="space-y-2">
              <Text className="text-sm font-semibold text-typography-900">
                {i18n.t("panels.panelName")}
              </Text>
              <Input
                className="border border-outline-200 rounded-xl bg-background-50"
                isDisabled={saving}
              >
                <InputField
                  defaultValue={name}
                  key={panel?.id ?? "new"}
                  maxLength={100}
                  onChangeText={(text) => {
                    nameRef.current = text;
                    setCanSaveName(!!text.trim());
                  }}
                  onSubmitEditing={() => {
                    if (!isSaveDisabled() && !saving) {
                      handleSave();
                    }
                  }}
                  placeholder={i18n.t("panels.panelName")}
                />
              </Input>
              <Checkbox
                isChecked={isDefault}
                isDisabled={saving}
                onChange={setIsDefault}
                value="isDefault"
              >
                <CheckboxIndicator className="data-[checked=true]:bg-primary-500 data-[checked=true]:border-primary-500">
                  <CheckboxIcon as={Check} className="text-white" size="sm" />
                </CheckboxIndicator>
                <CheckboxLabel className="text-sm font-medium text-typography-800">
                  {i18n.t("panels.setAsDefault")}
                </CheckboxLabel>
              </Checkbox>
            </VStack>

            <VStack className="space-y-2">
              <DemographicsEditor
                demographics={demographics}
                disabled={saving}
                onChange={setDemographics}
              />
            </VStack>
          </VStack>
        </AlertDialogBody>
        <AlertDialogFooter>
          <VStack className="gap-2">
            <HStack className="gap-4 justify-end">
              <Button
                className="px-6 py-3 rounded-lg"
                isDisabled={saving}
                onPress={onClose}
                variant="outline"
              >
                <ButtonText className="text-typography-700">
                  {i18n.t("misc.cancel")}
                </ButtonText>
              </Button>
              <Button
                className="px-6 py-3 rounded-lg bg-primary-500"
                isDisabled={saving || isSaveDisabled()}
                onPress={handleSave}
              >
                <ButtonText className="text-white">
                  {saving
                    ? panel?.id
                      ? i18n.t("misc.saving")
                      : i18n.t("misc.creating")
                    : panel?.id
                      ? i18n.t("misc.save")
                      : i18n.t("misc.create")}
                </ButtonText>
              </Button>
            </HStack>
            {errorMessage && (
              <Text className="text-error-600 text-sm text-right">
                {errorMessage}
              </Text>
            )}
          </VStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PanelEditor;
